/* eslint-disable no-unused-vars */

/* eslint-disable react/prop-types */

/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Icon from 'components/shared/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ labeled, bottomSpace, ...props }, ref) => <Form.Group ref={ref} {...props} />)
)`
  position: relative;
  padding-top: ${({ labeled }) => (labeled ? spaces.medium : 0)}px;

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${typeof bottomSpace === 'string' ? bottomSpace : `${bottomSpace}px`};
    `}
`;

const StyledLabel = styled(forwardRef(({ textColor, invalid, ...props }, ref) => <Form.Label ref={ref} {...props} />))`
  position: absolute;
  top: 0;
  left: ${spaces.smallest}px;
  padding-left: ${spaces.smallX}px;
  padding-right: ${spaces.smallX}px;
  margin: 0;
  background-color: ${colors.white};

  font-family: 'RethinkSans';
  font-size: ${fontSizes.small}px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.gray.secondary};

  z-index: 1;

  ${({ textColor }) =>
    textColor &&
    css`
      color: ${textColor};

      span {
        color: ${textColor};
      }
    `}

  ${({ invalid }) =>
    invalid &&
    css`
      color: ${colors.red.primary};
    `}
`;

const StyledControl = styled(
  forwardRef(
    (
      { textColor, placeholderColor, borderColor, backgroundColor, processing, icon, iconSpace, small, ...props },
      ref
    ) => <Form.Control ref={ref} {...props} />
  )
)`
  padding-top: ${spaces.smallX}px;
  padding-bottom: ${spaces.smallX}px;
  padding-left: ${spaces.regular}px;
  padding-right: ${spaces.regular}px;

  border: 1px solid ${hexToRGBA(colors.gray.secondary, 0.16)};
  border-radius: 6px;
  background-color: ${colors.white};
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);

  font-family: 'RethinkSans';
  font-size: ${fontSizes.regular}px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${colors.gray.secondary};
  caret-color: ${colors.gray.secondary};

  ${({ small }) =>
    small &&
    css`
      padding-top: 0;
      padding-bottom: 0;
      border-radius: 4px;
    `}

  ${({ icon }) =>
    icon &&
    css`
      padding-left: ${spaces.large2X}px;
    `}

  ${({ icon, iconSpace }) =>
    icon &&
    iconSpace &&
    css`
      padding-left: ${iconSpace}px;
    `}

  ${({ textColor }) =>
    textColor &&
    css`
      color: ${textColor};
      caret-color: ${textColor};
    `}

  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      background-color: ${backgroundColor};
    `}

  ${({ borderColor }) =>
    borderColor &&
    css`
      border-color: ${borderColor};
    `}

  &::placeholder {
    color: ${hexToRGBA(colors.gray.secondary, 0.5)};

    ${({ placeholderColor }) =>
      placeholderColor &&
      css`
        color: ${placeholderColor};
      `}
  }

  &:focus {
    border-color: ${colors.gray.secondary};
    box-shadow: none;
    outline: none;

    ${({ textColor }) =>
      textColor &&
      css`
        border-color: ${textColor};
        color: ${textColor};
        caret-color: ${textColor};
      `}

    ${({ backgroundColor }) =>
      backgroundColor &&
      css`
        background-color: ${backgroundColor};
      `}
  }

  &:disabled {
    background-color: ${colors.white};
    opacity: 0.4;
  }

  &.form-control.is-valid {
    padding-right: ${spaces.regular}px;
    border-color: ${hexToRGBA(colors.gray.secondary, 0.16)};
    background-image: none;

    &:focus {
      border-color: ${colors.gray.secondary};
      box-shadow: none;
      outline: none;
    }
  }

  &.form-control.is-invalid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.red.primary};
    background-image: none;

    &:focus {
      border-color: ${colors.gray.secondary};
      box-shadow: none;
      outline: none;
    }
  }

  ${({ processing }) =>
    processing &&
    css`
      padding-right: ${spaces.large6X}px;
    `};

  &::-webkit-file-upload-button {
    background-color: inherit !important;
  }

  &::file-selector-button {
    background-color: inherit !important;
  }

  &:hover::file-selector-button {
    text-decoration: underline;
  }

  &::-webkit-autofill,
  &::-webkit-contacts-auto-fill-button,
  &::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }
`;

const StyledSpinner = styled(({ labeled, color, small, ...props }) => <Spinner {...props} />)`
  position: absolute;
  top: ${spaces.medium + (42 - 32) / 2}px;
  right: ${spaces.small2X}px;

  &.text-custom {
    color: ${colors.gray.secondary};

    ${({ color }) =>
      color &&
      css`
        color: ${color};
      `};
  }

  ${({ labeled }) =>
    !labeled &&
    css`
      top: ${(42 - 32) / 2}px;
    `}

  ${({ small }) =>
    small &&
    css`
      top: ${spaces.medium + (28 - 24) / 2}px;
      height: 24px;
      width: 24px;
    `}
  
  ${({ labeled, small }) =>
    !labeled &&
    small &&
    css`
      top: ${(28 - 24) / 2}px;
    `}
`;

const StyledIconContainer = styled(
  forwardRef(({ labeled, left, right, small, ...props }, ref) => <div ref={ref} {...props} />)
)`
  display: flex;
  align-items: center;
  position: absolute;
  top: ${spaces.medium}px;
  right: ${spaces.smallX}px;
  height: 42px;

  cursor: pointer;

  ${({ small }) =>
    small &&
    css`
      height: 28px;
    `}

  ${({ labeled }) =>
    !labeled &&
    css`
      top: 0;
    `}
  
  ${({ left }) =>
    left &&
    css`
      right: unset;
      left: ${left}px;
    `}

  ${({ right }) =>
    right &&
    css`
      right: ${right}px;
      left: unset;
    `}
`;

// eslint-disable-next-line react/display-name, react/prop-types, no-unused-vars
const StyledFeedback = styled(forwardRef(({ color, ...props }, ref) => <Form.Control.Feedback ref={ref} {...props} />))`
  padding-left: ${spaces.smallest}px;

  font-family: 'RethinkSans';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  &.processing-feedback {
    color: ${colors.gray.secondary};

    ${({ color }) =>
      color &&
      css`
        color: ${color};
      `};
  }

  &.valid-feedback {
    color: ${colors.green.primary};
  }

  &.invalid-feedback {
    color: ${colors.red.primary};
  }
`;

// eslint-disable-next-line react/display-name, react/prop-types, no-unused-vars
const BaseInput = forwardRef(
  (
    {
      name,
      type,
      accept,
      hint,
      icon,
      iconSpace,
      label,
      loading,
      labelSpace,
      required,
      bottomSpace,
      textColor,
      placeholderColor,
      borderColor,
      backgroundColor,
      small,
      valid,
      invalid,
      feedback,
      value,
      onChange,
      ...restProps
    },
    ref
  ) => {
    const [eye, setEye] = useState(true);

    const handleEye = () => {
      setEye((prev) => !prev);
    };

    const handleChangeFile = useCallback(
      (e) => {
        const { files } = e.target;
        onChange &&
          onChange({
            target: { name, value: files?.length ? files[0] : null },
          });
      },
      [name, onChange]
    );

    const overlayTriggerProps = useMemo(() => {
      const props = {
        placement: isMobile ? 'top' : 'right-start',
        delay: { show: 0, hide: 0 },
        trigger: ['hover', 'focus'],
        overlay: (
          <Tooltip id={`tooltip-${name}`} className="hintTooltip">
            {hint}
          </Tooltip>
        ),
      };

      if (!hint || isMobile) {
        props.show = false;
      }

      return props;
    }, [name, hint]);

    return (
      <OverlayTrigger {...overlayTriggerProps}>
        <StyledGroup
          labeled={Boolean(label || labelSpace)}
          bottomSpace={bottomSpace}
          className={invalid ? 'custom-control-invalid' : ''}
        >
          {label && (
            <StyledLabel invalid={invalid} textColor={textColor}>
              {label}
              {label && required && <span>&nbsp;*</span>}
            </StyledLabel>
          )}
          <StyledControl
            {...restProps}
            ref={ref}
            type={type === 'password' && !eye ? 'text' : type}
            accept={accept}
            isValid={valid}
            isInvalid={invalid}
            processing={loading}
            icon={icon}
            iconSpace={iconSpace}
            textColor={textColor}
            placeholderColor={placeholderColor}
            borderColor={borderColor}
            backgroundColor={backgroundColor}
            small={small}
            value={type === 'file' ? undefined : value || ''}
            onChange={type === 'file' ? handleChangeFile : onChange}
            data-hj-allow
          />
          {icon && (
            <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small} left={spaces.smallX}>
              {icon}
            </StyledIconContainer>
          )}
          {loading && (
            <>
              <StyledSpinner
                animation="border"
                variant="custom"
                labeled={Boolean(label || labelSpace)}
                color={placeholderColor}
                small={small}
              />
              {feedback && (
                <StyledFeedback type="processing" color={textColor}>
                  {feedback}
                </StyledFeedback>
              )}
            </>
          )}
          {type === 'file' && value?.name && (
            <StyledFeedback type="processing" color={textColor}>
              {value.name}
            </StyledFeedback>
          )}
          {!loading && valid && (
            <>
              <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small}>
                <Icon alias={ICON_ALIASES.tickSquare} color={colors.green.primary} />
              </StyledIconContainer>
              {feedback && <StyledFeedback type="valid">{feedback}</StyledFeedback>}
            </>
          )}
          {!loading && invalid && (
            <>
              <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small}>
                <Icon alias={ICON_ALIASES.closeSquare} color={colors.red.primary} />
              </StyledIconContainer>
              {feedback && <StyledFeedback type="invalid">{feedback}</StyledFeedback>}
            </>
          )}
          {!loading && !valid && !invalid && type === 'file' && (
            <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small}>
              <Icon alias={ICON_ALIASES.document} color={placeholderColor || colors.gray.secondary} />
            </StyledIconContainer>
          )}
          {!loading && !valid && !invalid && type === 'password' && eye && (
            <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small} onClick={handleEye}>
              <Icon alias={ICON_ALIASES.eye} color={colors.cyanBlue} />
            </StyledIconContainer>
          )}
          {!loading && (valid || invalid) && type === 'password' && eye && (
            <StyledIconContainer
              labeled={Boolean(label || labelSpace)}
              small={small}
              right={spaces.large3X}
              onClick={handleEye}
            >
              <Icon alias={ICON_ALIASES.eye} color={colors.cyanBlue} />
            </StyledIconContainer>
          )}
          {!loading && !valid && !invalid && type === 'password' && !eye && (
            <StyledIconContainer labeled={Boolean(label || labelSpace)} small={small} onClick={handleEye}>
              <Icon alias={ICON_ALIASES.eyeSlash} color={colors.gray.secondary} />
            </StyledIconContainer>
          )}
          {!loading && (valid || invalid) && type === 'password' && !eye && (
            <StyledIconContainer
              labeled={Boolean(label || labelSpace)}
              small={small}
              right={spaces.large3X}
              onClick={handleEye}
            >
              <Icon alias={ICON_ALIASES.eyeSlash} color={colors.gray.secondary} />
            </StyledIconContainer>
          )}
        </StyledGroup>
      </OverlayTrigger>
    );
  }
);

BaseInput.propTypes = {
  name: PropTypes.string,
  type: PropTypes.string,
  accept: PropTypes.string,
  hint: PropTypes.node,
  icon: PropTypes.node,
  iconSpace: PropTypes.number,
  label: PropTypes.string,
  labelSpace: PropTypes.bool,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  bottomSpace: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textColor: PropTypes.string,
  placeholderColor: PropTypes.string,
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  small: PropTypes.bool,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default BaseInput;
